import React, { Fragment, useState, useEffect, useRef, ChangeEvent } from 'react';
import parse from 'html-react-parser';
import axios from 'axios';

import pidController from '../../../controllers/pidController';
import generalController from '../../../controllers/generalController';
import { fetchModelData } from '../../../controllers/ActionController';
import { getEndpointUrl } from '../../../utils/endPoints';
import { pushToGTMDataLayer, closeModal, convertLangCodeToSingleLetter, handleAnalyticsTrackingCodes, capitalizeWords } from '../../../utils/helpers';
import { languageController } from '../../../controllers/language/languageController';
import { useForm } from '../../../hooks/useForm';
import formLang from '../../../controllers/language/shared/form';

import Input from '../../1_Atoms/Forms/Input/Input';
import Checkbox from '../../1_Atoms/Forms/Checkbox/Checkbox';
import Select from '../../1_Atoms/Forms/Select/Select';
import Textarea from '../../1_Atoms/Forms/Textarea/Textarea';
import Button from '../../1_Atoms/Button/Button';
import FormGroup from '../../2_Molecules/FormGroup/FormGroup';
import Form from '../../2_Molecules/Form/Form';
import FormContainer from '../../2_Molecules/FormContainer/FormContainer';
import FormConfirmation from '../../2_Molecules/FormConfirmation/FormConfirmation';
import Modal from '../../2_Molecules/Modal/Modal';

interface FormExtraParams {
  language: string;
  formDisclaimer?: string;
  source: string;
  modal?: string | null;
}

interface SendBuildToDealerProps {
  formExtraParams: FormExtraParams;
}

interface SendBuildFormData {
  [key: string]: string | number | boolean;
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  optin: boolean;
  phone: string;
  preferredContact: string;
  message: string;
}

interface InitVehicleData {
  year: string;
  model: string;
  title: string;
}

interface ApiResponse {
  Success?: boolean;
  Errors?: string[];
}

interface VehicleDataJSON {
  year: string;
  carline: string;
  trim: string;
  modelcode: string;
  json: {
    bodystyle: string;
    options: {
      transmission: {
        en: string;
      };
    };
    msrp: number;
    allinpricing: {
      freightpde: {
        price: string;
      };
    };
  };
  colour: {
    name: {
      en: string;
    };
    interiorColours: Array<{
      name: {
        en: string;
      };
    }>;
  };
  tradein: string;
  downpayment: string;
  creditTotal: number;
  cash: {
    subtotal: number;
  };
  OptionName: string;
  OptionCode: string;
  apr: {
    selected: {
      months: number | string;
      rate: number | string;
      approxMonthly: number | string;
      approxBiweekly: number | string;
      approxWeekly: number | string;
    };
  };
  lease: {
    selected: {
      months: number | string;
      rate: number | string;
      approxMonthly: number | string;
      approxBiweekly: number | string;
      approxWeekly: number | string;
    };
  };
}

interface RequestData {
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  phone: string;
  preferredContact: string;
  source: string;
  language: string;
  optin: string;
  Message?: string;
  Message_fr?: string;
  year?: string;
  model?: string;
  Incentive?: number;
  OptionTotal?: number;
  data?: string;
  pid?: string;
  dealerID?: string;
}

declare global {
  interface Window {
    bnp_retailer_mode?: boolean;
    summaryCalculator?: any;
  }
}

const SendBuildToDealer: React.FC<SendBuildToDealerProps> = ({ formExtraParams }) => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isModal, setIsModal] = useState<boolean | null>(null);
  const [initVehicleData, setInitVehicleData] = useState<InitVehicleData | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const { language, formDisclaimer, source, modal } = formExtraParams;

  const [dealerID, setDealerID] = useState<string | null>(null);
  const [dealerName, setDealerName] = useState<string | null>(null);

  const urlparams = generalController.getURLSearchParams();

  useEffect(() => {
    if (window.bnp_retailer_mode) {
      // Set the polling until we have a dealer vars with the information set as we should always have the dealer vars information
      const intervalRef = setInterval(() => {
        const dealerVars = window.summaryCalculator?.getDealerVars();

        if (dealerVars) {
          setDealerID(dealerVars.dealerId);
          setDealerName(capitalizeWords(dealerVars.dealerName));

          clearInterval(intervalRef);
        }
      }, 200);
    }
    if (modal !== null && modal !== undefined) {
      setIsModal(modal === 'true');
    }
  }, [modal]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isModal) {
        const modelParam = decodeURI(urlparams.model).trim();
        const modelParamData = modelParam.split('|');

        if (modelParamData[0] && modelParamData[1]) {
          const result = await fetchModelData(modelParamData[0], modelParamData[1]);
          if (result && typeof result === 'object') {
            setInitVehicleData(result as InitVehicleData);
          }
        }
      }
    };
    fetchData();
  }, [isModal]);

  const initState: SendBuildFormData = {
    firstname: '',
    lastname: '',
    email: '',
    postalcode: '',
    optin: false,
    phone: '',
    preferredContact: 'Email',
    message: ''
  };

  const [phoneValue, setPhoneValue] = useState<string>('');
  const [validations, setValidations] = useState<Record<string, string[]>>({
    firstname: ['required', 'name', 'nameMaxMin'],
    lastname: ['required', 'name', 'nameMaxMin'],
    email: ['required', 'email'],
    phone: ['phone'],
    postalcode: ['required', 'postalcode'],
  });

  const transformIntoValidValue = (value: string | number): string => {
    if (typeof value !== "string") {
      value = String(value);
    }

    let transformedValue = value;
    transformedValue = transformedValue.replace(/\s/g, '').replace(',', '').replace('$', '');

    return transformedValue;
  };

  const transformIntoValidRoundedValue = (value: string | number): number => {
    if (typeof value !== "string") {
      value = String(value);
    }

    let normalized = value.replace(/\s/g, '').replace(',', '.');
    return Math.round(parseFloat(normalized));
  };

  const { handleSubmit, handleChange, data, errors, setData } = useForm({
    initState,
    validations: validations as any,
    // Send the form data if validated
    onSubmit: async () => {
      try {

        function getMessageContent(currentLang: string): string {
          const hashLength = window.location.hash.split('/').length;
          let messageContent = '';
          let currentStep = 'Trim';

          let annualKm = '16,000';

          if (hashLength < 7) {
            currentStep = 'Options';
          } else {
            currentStep = window.location.hash.split('/')[6].split('?')[0];
          }

          if (window.summaryCalculator?.AnnualMileage === 0) {
            annualKm = '24,000';
          } else if (window.summaryCalculator?.AnnualMileage === 2) {
            annualKm = '20,000';
          }

          if (currentLang === 'en') {
            messageContent = `
              B&P Step: ${currentStep}
              Purchase Method Button: ${window.summaryCalculator?.CurrentPurchaseMethod ? window.summaryCalculator.CurrentPurchaseMethod : ''}
              Wheels: ${window.summaryCalculator?.SelectedWheel ? `|${window.summaryCalculator.SelectedWheel.image.overlay_image_suffix}|` : ''}
              Accessories: ${window.summaryCalculator?.SelectedAccessories?.map((item: any) => { return `|${item.part_number}|` }).join()}
              Mazda Owner Loyalty Program: ${window.summaryCalculator?.LoyaltyChecked}
              Mazda Graduate Program Award: ${window.summaryCalculator?.GraduateChecked}
              Mazda Military Program: ${window.summaryCalculator?.MilitaryChecked}
              Annual KM: ${annualKm}
              Option Code: ${window.summaryCalculator?.selectedVehicle?.packageCode ? window.summaryCalculator.selectedVehicle.packageCode : ''}
              Option Name: ${window.summaryCalculator?.SelectedPackage && window.summaryCalculator?.SelectedPackage.option_code !== undefined ? `|${window.summaryCalculator.SelectedPackage.option_code}|` : 'Default'}
              Lease Weekly: ${window.summaryCalculator?.FinancialInfo?.calculatedLease?.leaseValues ? transformIntoValidRoundedValue(window.summaryCalculator.FinancialInfo.calculatedLease.leaseValues[window.summaryCalculator.LeaseTermIndex][window.summaryCalculator.IncludeTaxes ? 'weeklyWithTaxes' : 'weekly']) : ''}
            `;
            if (window.summaryCalculator?.IncludeRebates) {
              messageContent += `Include Government Rebates: ${window.summaryCalculator.Incentives.incentive.value}
              `;
            }
            messageContent += `
              Include fees and levies: ${window.summaryCalculator?.IncludeFees}
              Include taxes: ${window.summaryCalculator?.IncludeTaxes}
              Comments: ${data.message}
            `;
          } else {
            annualKm = annualKm.replace(',', ' ');

            const frenchSteps: Record<string, string> = {
              "Trim": "Version",
              "Options": "Options",
              "colour": "Couleur",
              "wheels": "Roues",
              "accessories": "Accessoires",
              "payments": "Prix et paiements",
              "summary": "Récapitulatif"
            };

            messageContent = `
              B&P Étape: ${frenchSteps[currentStep]}
              Méthode d'achat Bouton: ${window.summaryCalculator?.CurrentPurchaseMethod ?
                window.summaryCalculator.CurrentPurchaseMethod === 'Lease' ?
                  'Location' :
                  'Financement'
                : ''}
              Roues: ${window.summaryCalculator?.SelectedWheel ? `|${window.summaryCalculator.SelectedWheel.image.overlay_image_suffix}|` : ''}
              Accessoires: ${window.summaryCalculator?.SelectedAccessories?.map((item: any) => { return `|${item.part_number}|` }).join(',')}
              Programme renouvellement de fidélité Mazda: ${window.summaryCalculator?.LoyaltyChecked}
              Programme de diplômés Mazda: ${window.summaryCalculator?.GraduateChecked}
              Programme Mazda pour militaires: ${window.summaryCalculator?.MilitaryChecked}
              Kilométrage Annuel: ${annualKm}
              Code d'option: ${window.summaryCalculator?.selectedVehicle?.packageCode ? window.summaryCalculator.selectedVehicle.packageCode : ''}
              Nom de l'option: ${window.summaryCalculator?.SelectedPackage && `|${window.summaryCalculator?.SelectedPackage.option_code}|` !== undefined ? window.summaryCalculator.SelectedPackage.option_code : 'défaut'}
              Location hebdomadaire: ${window.summaryCalculator?.FinancialInfo?.calculatedLease?.leaseValues ? transformIntoValidRoundedValue(window.summaryCalculator.FinancialInfo.calculatedLease.leaseValues[window.summaryCalculator.LeaseTermIndex][window.summaryCalculator.IncludeTaxes ? 'weeklyWithTaxes' : 'weekly']) : ''}
            `;
            if (window.summaryCalculator?.IncludeRebates) {
              messageContent += `Inclure les rabais gouvernementaux: ${window.summaryCalculator.Incentives.incentive.value}
              `;
            }
            messageContent += `
              Inclure les frais et les prélèvements: ${window.summaryCalculator?.IncludeFees}
              Inclure les taxes: ${window.summaryCalculator?.IncludeTaxes}
              Commentaires: ${data.message}
            `;
          }
          return messageContent;
        }

        const reqData: RequestData = {
          firstname: String(data.firstname),
          lastname: String(data.lastname),
          email: String(data.email),
          postalcode: String(data.postalcode),
          phone: String(data.phone),
          preferredContact: String(data.preferredContact),
          source: source,
          language: convertLangCodeToSingleLetter(language as any),
          optin: data.optin ? 'on' : 'off',
        };

        if (typeof (window.summaryCalculator) === 'object') {
          const wheelPrice = window.summaryCalculator?.SelectedWheel ? window.summaryCalculator.SelectedWheel.price : 0;

          reqData.Incentive = window.summaryCalculator?.FinancialInfo?.calculatedCash?.specialIncentives ? window.summaryCalculator.FinancialInfo.calculatedCash.specialIncentives : 0;
          reqData.OptionTotal = wheelPrice
            + window.summaryCalculator.SelectedNonModelPackage.financial.msrp
            + window.summaryCalculator.SelectedWarrantyPackage.financial.msrp
            + window.summaryCalculator.SelectedConnectedVehiclePackage.financial.msrp
            + window.summaryCalculator.SelectedAddedProtectionPackage.financial.msrp
            + window.summaryCalculator.SelectedAppearanceProtectionPlansPackage.financial.msrp
            + window.summaryCalculator.FinancialInfo.calculatedFinance.accessories.total
            + window.summaryCalculator.SelectedColourInterior.price
            + window.summaryCalculator.SelectedColourExterior.price;
          reqData.Message = getMessageContent('en');
          reqData.Message_fr = getMessageContent('fr');

          if (window.summaryCalculator?.FinancialInfo?.calculatedCash?.specialIncentives) {
            reqData.Incentive = window.summaryCalculator.FinancialInfo.calculatedCash.specialIncentives;
          }
        } else {
          reqData.Message = String(data.message);
        }

        // Vehicle
        if (urlparams.model) {
          reqData.year = initVehicleData?.year;
          reqData.model = initVehicleData?.model;
        } else {
          const selectedVehicle = sessionStorage.getItem("EngineAndDrivetrain");

          if (selectedVehicle) {
            const selectedVehicleParsed = JSON.parse(selectedVehicle);
            reqData.year = selectedVehicleParsed.model_year.year.id;
            reqData.model = selectedVehicleParsed.model_year.carline.id;
          }
        }

        function createJsonOutput(): string {
          const vehicleDataJSON: VehicleDataJSON = {
            year: '',
            carline: '',
            trim: '',
            modelcode: '',
            json: {
              bodystyle: '',
              options: {
                transmission: {
                  en: ''
                }
              },
              msrp: 0,
              allinpricing: {
                freightpde: {
                  price: ''
                }
              }
            },
            colour: {
              name: {
                en: ''
              },
              interiorColours: [
                {
                  name: {
                    en: ''
                  }
                }
              ]
            },
            tradein: '',
            downpayment: '',
            creditTotal: 0,
            cash: {
              subtotal: 0
            },
            OptionName: '',
            OptionCode: '',
            apr: {
              selected: {
                months: '',
                rate: '',
                approxMonthly: '',
                approxBiweekly: '',
                approxWeekly: ''
              }
            },
            lease: {
              selected: {
                months: '',
                rate: '',
                approxMonthly: '',
                approxBiweekly: '',
                approxWeekly: ''
              }
            }
          };

          vehicleDataJSON.year = reqData.year || '';
          vehicleDataJSON.carline = reqData.model || '';

          vehicleDataJSON.trim = window.summaryCalculator?.SelectedTrim.trim_type.id ? window.summaryCalculator.SelectedTrim.trim_type.id : '';
          vehicleDataJSON.json.options.transmission.en = window.summaryCalculator?.SelectedModel.drivetrain.id ? window.summaryCalculator.SelectedModel.transmission.id : '';
          vehicleDataJSON.modelcode = window.summaryCalculator?.SelectedModel?.model_code ? window.summaryCalculator.SelectedModel.model_code : '';
          vehicleDataJSON.colour.name.en = window.summaryCalculator?.SelectedColourExterior.id;
          vehicleDataJSON.colour.interiorColours[0].name.en = window.summaryCalculator?.SelectedColourInterior.id;

          vehicleDataJSON.json.msrp = window.summaryCalculator?.SelectedTrim?.financial?.msrp ? window.summaryCalculator.SelectedTrim.financial.msrp : 0;

          vehicleDataJSON.tradein = transformIntoValidValue(window.summaryCalculator?.TradeInField ? window.summaryCalculator.TradeInField : 0);
          vehicleDataJSON.downpayment = transformIntoValidValue(window.summaryCalculator?.DownPaymentValue ? window.summaryCalculator.DownPaymentValue : 0);

          vehicleDataJSON.creditTotal = window.summaryCalculator?.FinancialInfo?.calculatedCash?.specialIncentives ? transformIntoValidRoundedValue(window.summaryCalculator.FinancialInfo.calculatedCash.specialIncentives) : 0;

          let subtotal = window.summaryCalculator?.CurrentCalculatedCash;
          subtotal = subtotal.replace(/\s/g, '').replace(',', '.');
          let integerSubtotal = Math.floor(Number(subtotal));

          vehicleDataJSON.cash.subtotal = integerSubtotal;

          vehicleDataJSON.OptionName = window.summaryCalculator?.SelectedPackage && window.summaryCalculator?.SelectedPackage.option_id !== undefined ? window.summaryCalculator.SelectedPackage.option_id : 'Default';
          vehicleDataJSON.OptionCode = window.summaryCalculator?.selectedVehicle?.packageCode ? window.summaryCalculator.selectedVehicle.packageCode : '';

          vehicleDataJSON.apr.selected.months = window.summaryCalculator?.FinanceTerm ? window.summaryCalculator.FinanceTerm : '';

          let loanRate = window.summaryCalculator?.FinancialInfo?.calculatedFinance?.financeValues ? window.summaryCalculator?.FinancialInfo.calculatedFinance.financeValues[window.summaryCalculator.FinanceTermIndex].aprRate : '';
          if (window.summaryCalculator?.LoyaltyChecked) {
            loanRate = window.summaryCalculator?.FinancialInfo?.calculatedFinance?.financeValues ? window.summaryCalculator?.FinancialInfo.calculatedFinance.financeValues[window.summaryCalculator.FinanceTermIndex].aprRateDiscounted : '';
          }
          vehicleDataJSON.apr.selected.rate = loanRate;
          vehicleDataJSON.apr.selected.approxMonthly = window.summaryCalculator?.FinancialInfo?.calculatedFinance?.financeValues ? transformIntoValidRoundedValue(window.summaryCalculator?.FinancialInfo.calculatedFinance.financeValues[window.summaryCalculator.FinanceTermIndex][window.summaryCalculator.IncludeTaxes ? 'monthlyWithTaxes' : 'monthly']) : '';
          vehicleDataJSON.apr.selected.approxBiweekly = window.summaryCalculator?.FinancialInfo?.calculatedFinance?.financeValues ? transformIntoValidRoundedValue(window.summaryCalculator?.FinancialInfo.calculatedFinance.financeValues[window.summaryCalculator.FinanceTermIndex][window.summaryCalculator.IncludeTaxes ? 'biWeeklyWithTaxes' : 'biWeekly']) : '';
          vehicleDataJSON.apr.selected.approxWeekly = window.summaryCalculator?.FinancialInfo?.calculatedFinance?.financeValues ? transformIntoValidRoundedValue(window.summaryCalculator?.FinancialInfo.calculatedFinance.financeValues[window.summaryCalculator.FinanceTermIndex][window.summaryCalculator.IncludeTaxes ? 'weeklyWithTaxes' : 'weekly']) : '';

          vehicleDataJSON.lease.selected.months = window.summaryCalculator?.LeaseTerm ? window.summaryCalculator.LeaseTerm : '';
          let leaseRate = window.summaryCalculator?.FinancialInfo.calculatedLease.leaseValues[window.summaryCalculator.LeaseTermIndex].leaseRate;
          if (window.summaryCalculator?.LoyaltyChecked) {
            leaseRate = window.summaryCalculator?.FinancialInfo.calculatedLease.leaseValues[window.summaryCalculator.LeaseTermIndex].leaseRateDiscounted;
          }
          vehicleDataJSON.lease.selected.rate = leaseRate;
          vehicleDataJSON.lease.selected.approxMonthly = window.summaryCalculator?.FinancialInfo?.calculatedLease?.leaseValues ? transformIntoValidRoundedValue(window.summaryCalculator.FinancialInfo.calculatedLease.leaseValues[window.summaryCalculator.LeaseTermIndex][window.summaryCalculator.IncludeTaxes ? 'monthlyWithTaxes' : 'monthly']) : '';
          vehicleDataJSON.lease.selected.approxBiweekly = window.summaryCalculator?.FinancialInfo?.calculatedLease?.leaseValues ? transformIntoValidRoundedValue(window.summaryCalculator.FinancialInfo.calculatedLease.leaseValues[window.summaryCalculator.LeaseTermIndex][window.summaryCalculator.IncludeTaxes ? 'biWeeklyWithTaxes' : 'biWeekly']) : '';
          vehicleDataJSON.lease.selected.approxWeekly = window.summaryCalculator?.FinancialInfo?.calculatedLease?.leaseValues ? transformIntoValidRoundedValue(window.summaryCalculator.FinancialInfo.calculatedLease.leaseValues[window.summaryCalculator.LeaseTermIndex][window.summaryCalculator.IncludeTaxes ? 'weeklyWithTaxes' : 'weekly']) : '';

          return JSON.stringify(vehicleDataJSON);
        }

        if (typeof (window.summaryCalculator) === 'object') {
          reqData.data = createJsonOutput();
        }

        if (isModal) {
          reqData.pid = String(pidController.getPid('contactDealer'));
        } else {
          reqData.pid = String(pidController.getPid('getLocalPrice'));
        }

        if (dealerID) {
          reqData.dealerID = dealerID;
        }

        if (!reqData?.model) {
          showFailureMessage();
          return;
        }

        const params = new URLSearchParams(reqData as any);
        const endpoint = getEndpointUrl('contact-dealer');
        if (!endpoint) return;
        const res = await axios.post(endpoint, params);
        const resData: ApiResponse = res.data;

        if (resData && resData.Success) {
          setIsSubmitted(true);
          pushToGTMDataLayer({ formevent: 'submit' });
          const analyticsData: any = { ...data, optin: data.optin ? 'optin' : 'optout' };
          if (typeof (window.summaryCalculator) === 'object') {
            analyticsData.modelYear = window.summaryCalculator.selectedVehicle.year;
            analyticsData.modelCarlineName = window.summaryCalculator.selectedVehicle.carline;
          } else if (urlparams.model) {
            analyticsData.modelYear = initVehicleData?.year;
            analyticsData.modelCarlineName = initVehicleData?.model;
          }

          handleAnalyticsTrackingCodes(analyticsData, 'Form Submit', 'Send Build To Dealer Modal', 'SendBuildToDealer');
          setData(initState as any);
        } else {
          if (resData.Errors && resData.Errors.length > 0) {
            setFormError(resData.Errors[0]);
          }
        }
      } catch (err) {
        console.log(err);
      }
    },
  });

  const { firstname, lastname, email, postalcode, phone, preferredContact, message, optin } = data;

  function showFailureMessage() {
    // Placeholder for failure message logic
  }

  useEffect(() => {
    setValidations(prevValidations => {
      const newValidations = { ...prevValidations };
      if (phoneValue.length > 9) {
        newValidations.preferredContact = ['required'];
      } else {
        if (newValidations.preferredContact) {
          delete newValidations.preferredContact;
        }
      }
      return newValidations;
    });
  }, [phoneValue]);

  function getFormContent() {
    return (
      <FormContainer
        className="mz-send-build-to-dealer"
        isSubmitted={isSubmitted}
        heading={(
          <Fragment>
            <h3 className="text--center">{languageController.getCopy('sendBuildToDealer', 'heading')}</h3>

            <div className="mz-send-build-to-dealer__vehicle">
              <h4 className="mz-send-build-to-dealer__vehicle-name text--center">{initVehicleData ? language === 'en' ? parse(`${initVehicleData?.year} ${initVehicleData?.title}`) : parse(`${initVehicleData?.title} ${initVehicleData?.year}`) : ''}</h4>
            </div>
          </Fragment>
        )}
        confirmation={(
          <FormConfirmation
            heading={languageController.getCopy('sendBuildToDealer', 'confirmationHeading') || ''}
            subHeading={dealerID ? languageController.getCopy('sendBuildToDealer', 'confirmationSubHeadingRetailer') || '' : dealerName ? `${languageController.getCopy('sendBuildToDealer', 'confirmationSubHeading')} ${decodeURI(dealerName)}` : languageController.getCopy('sendBuildToDealer', 'confirmationSubHeading') || ''}
            buttonLabel={isModal ? languageController.getCopy('sendBuildToDealer', 'confirmationSubmit') : undefined}
            onClick={() => {
              if (modalRef.current) {
                closeModal(modalRef.current);
              }
              setIsSubmitted(false);
              setTimeout(() => {
                const submitButton = modalRef.current?.querySelector<HTMLButtonElement>('button[type="submit"]');
                if (submitButton) {
                  submitButton.disabled = false;
                }
              }, 1200);
            }}
          />
        )}
      >
        <Form
          onSubmit={handleSubmit}
          isDark={false}
          noValidate={true}
          errorMessage={formError || undefined}
        >
          <FormGroup md={2}>
            <Input
              type="text"
              name="firstname"
              label={languageController.getFormCopy('label', 'firstname')}
              value={String(firstname)}
              isRequired={true}
              errorMessage={errors.firstname}
              onChange={handleChange}
              maxLength={50}
            />
            <Input
              type="text"
              name="lastname"
              label={languageController.getFormCopy('label', 'lastname')}
              value={String(lastname)}
              isRequired={true}
              errorMessage={errors.lastname}
              onChange={handleChange}
              maxLength={50}
            />
          </FormGroup>
          <Input
            type="email"
            name="email"
            label={languageController.getFormCopy('label', 'email')}
            value={String(email)}
            isRequired={true}
            errorMessage={errors.email}
            onChange={handleChange}
            maxLength={320}
          />
          <FormGroup md={2}>
            <Input
              type="text"
              name="postalcode"
              label={languageController.getFormCopy('label', 'postalcode')}
              value={String(postalcode)}
              isRequired={true}
              errorMessage={errors.postalcode}
              onChange={handleChange}
              maxLength={7}
            />
          </FormGroup>

          <div className="mz-form-container__disclaimer label label--tight">
            <Checkbox
              name="optin"
              checked={Boolean(optin)}
              onChange={() => setData({ ...data, optin: !optin })}
              label={formDisclaimer ? parse(formDisclaimer) : ''}
            />
          </div>

          <div className="mz-container-flex">
            <Button
              type="submit"
              className="mz-btn--primary mz-btn--red"
              label={languageController.getCopy('sendBuildToDealer', 'submit')}
            />
          </div>
        </Form>
      </FormContainer>
    );
  }

  return (
    <>
      {isModal &&
        <Modal
          className="mz-modal-form mz-send-build-to-dealer-modal"
          id="mz-react-send-build-to-dealer-modal"
          onClose={() => {
            setIsSubmitted(false);
          }}
          modalRef={modalRef}
        >
          {getFormContent()}
        </Modal>
      }
      {!isModal &&
        <div className="mz-reserve-your-mazda">
          <div className="mz-container-flex">
            <div className="mz-full-width" style={{ maxWidth: '700px' }}>
              {getFormContent()}
            </div>
          </div>
        </div>
      }
    </>
  );
};

export default SendBuildToDealer;
